import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsAPI } from '../api/products';
import { CartContext } from '../context/CartContext';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getImageUrl, handleImageError } from '../utils/imageHelper';
import toast from 'react-hot-toast';
import Loader from '../components/common/Loader';
import ProductCard from '../components/common/ProductCard';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [latestProducts, setLatestProducts] = useState([]);
  const [latestLoading, setLatestLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { addToCart } = useContext(CartContext);

  useEffect(() => {
    fetchProduct();
    fetchLatestProducts();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const data = await productsAPI.getById(id);
      setProduct(data);
      if (data.sizes?.length) setSelectedSize(data.sizes[0].size);
      if (data.colors?.length) setSelectedColor(data.colors[0].color);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestProducts = async () => {
    try {
      const data = await productsAPI.getAll({ limit: 4 });
      // Filter out current product if present
      const filtered = data.filter(p => p.id.toString() !== id);
      setLatestProducts(filtered.slice(0, 4));
    } catch (error) {
      console.error('Error fetching latest products:', error);
    } finally {
      setLatestLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }
    if (!selectedColor) {
      toast.error('Please select a color');
      return;
    }
    
    const selectedSizeStock = product.sizes?.find(s => s.size === selectedSize)?.stock || 0;
    
    if (selectedSizeStock === 0) {
      toast.error(`Size ${selectedSize} is out of stock`);
      return;
    }
    
    if (quantity > selectedSizeStock) {
      toast.error(`Only ${selectedSizeStock} available`);
      return;
    }
    
    addToCart(product, selectedSize, selectedColor, quantity);
    toast.success('Added to cart');
  };

  const handleSizeChange = (size) => {
    setSelectedSize(size);
    setQuantity(1);
  };

  const getSelectedSizeStock = () => {
    if (!product?.sizes) return 0;
    const sizeObj = product.sizes.find(s => s.size === selectedSize);
    return sizeObj?.stock || 0;
  };

  const selectedSizeStock = getSelectedSizeStock();
  const isOutOfStock = selectedSizeStock === 0;
  const maxQuantity = Math.min(selectedSizeStock, 10);

  if (loading) return <Loader />;
  if (!product) return <div className="text-center py-20 text-gray-400 text-sm">Product not found</div>;

  const images = [product.main_image, ...(product.images?.map(img => img.image_url) || [])].filter(Boolean);
  const discount = product.discount_price 
    ? Math.round(((product.original_price - product.discount_price) / product.original_price) * 100)
    : 0;

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Image Gallery */}
          <div>
            <div 
              className="relative bg-gray-50 aspect-square cursor-pointer overflow-hidden"
              onClick={() => setLightboxOpen(true)}
            >
              <img
                src={getImageUrl(images[currentImage])}
                alt={product.title}
                className="w-full h-full object-cover"
                onError={(e) => handleImageError(e)}
              />
              {images.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImage((prev) => (prev > 0 ? prev - 1 : images.length - 1));
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 transition"
                  >
                    <ChevronLeftIcon className="h-5 w-5 text-gray-800" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImage((prev) => (prev < images.length - 1 ? prev + 1 : 0));
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 transition"
                  >
                    <ChevronRightIcon className="h-5 w-5 text-gray-800" />
                  </button>
                </>
              )}
            </div>
            
            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImage(idx)}
                    className={`w-20 h-20 bg-gray-50 overflow-hidden border ${
                      currentImage === idx ? 'border-gray-900' : 'border-gray-200'
                    }`}
                  >
                    <img 
                      src={getImageUrl(img)} 
                      alt="" 
                      className="w-full h-full object-cover"
                      onError={(e) => handleImageError(e)}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">
              {product.category?.name || 'Collection'}
            </p>
            
            <h1 className="text-2xl lg:text-3xl font-light tracking-tight text-gray-900 mb-4">
              {product.title}
            </h1>
            
            <div className="mb-6">
              {product.discount_price ? (
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-light text-gray-900">
                    ${parseFloat(product.discount_price).toFixed(2)}
                  </span>
                  <span className="text-sm text-gray-400 line-through">
                    ${parseFloat(product.original_price).toFixed(2)}
                  </span>
                  <span className="text-xs text-gray-500 border border-gray-200 px-2 py-0.5">
                    {discount}% off
                  </span>
                </div>
              ) : (
                <span className="text-2xl font-light text-gray-900">
                  ${parseFloat(product.original_price).toFixed(2)}
                </span>
              )}
            </div>

            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              {product.description}
            </p>

            <div className="mb-6">
              {isOutOfStock ? (
                <span className="text-xs text-gray-400">Out of stock</span>
              ) : selectedSizeStock <= 5 ? (
                <span className="text-xs text-gray-500">Only {selectedSizeStock} left</span>
              ) : (
                <span className="text-xs text-gray-400">In stock</span>
              )}
            </div>

            {product.sizes?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Size</h3>
                <div className="flex gap-2 flex-wrap">
                  {product.sizes.map((size) => {
                    const sizeStock = size.stock;
                    const isSizeOutOfStock = sizeStock === 0;
                    
                    return (
                      <button
                        key={size.size}
                        onClick={() => handleSizeChange(size.size)}
                        disabled={isSizeOutOfStock}
                        className={`min-w-[48px] h-10 text-sm border transition ${
                          selectedSize === size.size
                            ? 'border-gray-900 bg-gray-900 text-white'
                            : isSizeOutOfStock
                            ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        {size.size}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {product.colors?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Color</h3>
                <div className="flex gap-3 flex-wrap">
                  {product.colors.map((color) => (
                    <button
                      key={color.color}
                      onClick={() => setSelectedColor(color.color)}
                      className={`px-4 py-2 text-sm border transition ${
                        selectedColor === color.color
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {color.color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-8">
              <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Quantity</h3>
              <div className="flex items-center border border-gray-200 w-32">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={isOutOfStock}
                  className={`w-10 h-10 text-gray-500 transition ${
                    isOutOfStock ? 'text-gray-300 cursor-not-allowed' : 'hover:text-gray-900'
                  }`}
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  max={maxQuantity}
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val >= 1 && val <= maxQuantity) {
                      setQuantity(val);
                    }
                  }}
                  disabled={isOutOfStock}
                  className="w-12 text-center text-sm text-gray-900 border-x border-gray-200 py-2 focus:outline-none disabled:bg-gray-50"
                />
                <button
                  onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                  disabled={isOutOfStock || quantity >= maxQuantity}
                  className={`w-10 h-10 text-gray-500 transition ${
                    isOutOfStock || quantity >= maxQuantity ? 'text-gray-300 cursor-not-allowed' : 'hover:text-gray-900'
                  }`}
                >
                  +
                </button>
              </div>
            </div>

            <button
              onClick={handleAddToCart}
              disabled={isOutOfStock}
              className={`w-full py-3 text-sm tracking-wide transition ${
                isOutOfStock 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {isOutOfStock ? 'Sold Out' : 'Add to Cart'}
            </button>

            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="text-xs text-gray-400 space-y-2">
                <p>SKU: #{product.id}</p>
                <p>Free shipping on orders over $100</p>
                <p>30-day return policy</p>
              </div>
            </div>
          </div>
        </div>

        {/* Latest Products Section */}
        <div className="mt-20 pt-8 border-t border-gray-100">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-sm font-light tracking-wide uppercase text-gray-400">
                You May Also Like
              </h2>
              <div className="w-8 h-px bg-gray-300 mt-2"></div>
            </div>
            <Link 
              to="/products" 
              className="text-[10px] text-gray-400 hover:text-gray-900 uppercase tracking-wider transition"
            >
              View All →
            </Link>
          </div>
          
          {latestLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 gap-y-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-100 aspect-square"></div>
                  <div className="mt-3 space-y-2">
                    <div className="bg-gray-100 h-3 w-3/4"></div>
                    <div className="bg-gray-100 h-3 w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : latestProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8">
              {latestProducts.map((latestProduct) => (
                <ProductCard key={latestProduct.id} product={latestProduct} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-8">
              No related products found
            </p>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
          <button 
            onClick={() => setLightboxOpen(false)}
            className="absolute top-6 right-6 text-gray-400 hover:text-gray-900"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentImage((prev) => (prev > 0 ? prev - 1 : images.length - 1));
            }}
            className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900"
          >
            <ChevronLeftIcon className="h-8 w-8" />
          </button>
          <img 
            src={getImageUrl(images[currentImage])} 
            alt={product.title}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentImage((prev) => (prev < images.length - 1 ? prev + 1 : 0));
            }}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900"
          >
            <ChevronRightIcon className="h-8 w-8" />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-gray-400">
            {currentImage + 1} / {images.length}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductDetail;